import React, { useState, useRef, useContext, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Heart, Truck, Sparkles, Star, ShoppingCart } from 'lucide-react';
import { Badge } from './ui/badge';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { CartContext } from '../App';
import apiInstance from '../utils/axios';
import UserData from '../views/plugin/UserData';
import getCartID from '../views/plugin/cartID';
import GetCurrentAddress from '../views/plugin/UserCountry';
import toast from './ui/Toast';

const ProductCarousel = ({ 
  title, 
  products = [], 
  badge = null, 
  showViewAll = true,
  loading = false 
}) => {
  const { t, currentLanguage } = useLanguage();
  const { formatUSD } = useCurrency();
  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [addingProductId, setAddingProductId] = useState(null);
  const [wishlistedIds, setWishlistedIds] = useState({});
  const [wishlistLoadingId, setWishlistLoadingId] = useState(null);

  const navigate = useNavigate();
  const userData = UserData();
  const currentAddress = GetCurrentAddress();
  const cart_id = getCartID();
  const [cartCount, setCartCount] = useContext(CartContext);

  // Check arrow visibility on mount, resize, and product changes
  const checkArrows = useCallback(() => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollWidth > clientWidth + 10);
    }
  }, []);

  useEffect(() => {
    checkArrows();
    window.addEventListener('resize', checkArrows);
    return () => window.removeEventListener('resize', checkArrows);
  }, [products, checkArrows]);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 320;
      const newScrollLeft =
        direction === 'left'
          ? scrollRef.current.scrollLeft - scrollAmount
          : scrollRef.current.scrollLeft + scrollAmount;

      scrollRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      });
    }
  };

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const formatPrice = (price) => {
    return formatUSD(price);
  };

  const calculateDiscount = (price, oldPrice) => {
    if (!oldPrice || parseFloat(oldPrice) <= parseFloat(price)) return null;
    const discount = Math.round(((parseFloat(oldPrice) - parseFloat(price)) / parseFloat(oldPrice)) * 100);
    return discount > 0 ? discount : null;
  };

  const handleAddToCart = async (e, product) => {
    e.preventDefault();
    e.stopPropagation();

    if (addingProductId) return;

    const stock = product.stock_qty || product.stock || product.quantity || 0;
    if (stock <= 0) {
      toast.error(t('outOfStock') || 'Sin stock');
      return;
    }

    try {
      setAddingProductId(product.id || product.pid);
      const formdata = new FormData();
      formdata.append('product_id', product.id || product.pid);
      formdata.append('user_id', userData?.user_id || 0);
      formdata.append('qty', 1);
      formdata.append('price', product.price);
      formdata.append('shipping_ammount', product.shipping_ammount || product.shipping_amount || 0);
      formdata.append('country', currentAddress?.country || 'Paraguay');
      formdata.append('size', product.sizes?.[0]?.name || 'No Size');
      formdata.append('color', product.colors?.[0]?.name || 'No Color');
      formdata.append('cart_id', cart_id);

      await apiInstance.post('cart-view/', formdata);

      const url = userData?.user_id
        ? `cart-list/${cart_id}/${userData.user_id}/`
        : `cart-list/${cart_id}/`;
      const cartResponse = await apiInstance.get(url);
      const count = cartResponse.data?.cart_items?.length || cartResponse.data?.length || 0;
      setCartCount(count);

      toast.cart(t('addedToCart') || 'Agregado al carrito');
    } catch (error) {
      console.error('Error adding to cart:', error);
      let errorMessage = t('errorAddingToCart') || 'Error al agregar al carrito';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      toast.error(errorMessage);
    } finally {
      setAddingProductId(null);
    }
  };

  const handleWishlistToggle = async (e, product) => {
    e.preventDefault();
    e.stopPropagation();

    const productId = product.id || product.pid;

    if (!userData?.user_id) {
      toast.info(t('loginToAddWishlist') || 'Inicia sesión para agregar a favoritos');
      navigate('/login');
      return;
    }

    try {
      setWishlistLoadingId(productId);
      const formData = new FormData();
      formData.append('product_id', productId);
      formData.append('user_id', userData.user_id);

      const response = await apiInstance.post(`customer/wishlist/${userData.user_id}/`, formData);
      const wasWishlisted = wishlistedIds[productId];
      setWishlistedIds(prev => ({ ...prev, [productId]: !prev[productId] }));

      toast.wishlist(
        response.data.message || (wasWishlisted ? (t('removedFromWishlist') || 'Eliminado de favoritos') : (t('addedToWishlist') || 'Agregado a favoritos'))
      );
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      toast.error(t('errorWishlist') || 'Error al actualizar favoritos');
    } finally {
      setWishlistLoadingId(null);
    }
  };

  if (loading) {
    return (
      <section className="py-6 md:py-8">
        <div className="mx-auto max-w-7xl px-4">
          <div className="h-8 w-48 bg-secondary rounded animate-pulse mb-6" />
          <div className="flex gap-4 overflow-hidden">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex-shrink-0 w-[180px] md:w-[240px]">
                <div className="bg-card rounded-xl overflow-hidden border border-border">
                  <div className="aspect-square bg-secondary animate-pulse" />
                  <div className="p-3.5">
                    <div className="h-4 bg-secondary rounded animate-pulse mb-2" />
                    <div className="h-6 bg-secondary rounded animate-pulse mb-2" />
                    <div className="h-4 w-3/4 bg-secondary rounded animate-pulse" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  const safeProducts = Array.isArray(products) ? products : [];
  if (!safeProducts.length) return null;

  return (
    <section className="py-6 md:py-8">
      <div className="mx-auto max-w-7xl px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-5 md:mb-6">
          <div className="flex items-center gap-3">
            <h2 className="text-lg md:text-2xl font-bold text-foreground">{title}</h2>
            {badge && (
              <Badge variant="default" className="bg-accent text-accent-foreground text-xs font-semibold">
                {badge}
              </Badge>
            )}
          </div>
          {showViewAll && (
            <Link
              to="/products"
              className="text-sm text-accent hover:underline font-medium hidden md:block"
            >
              {t('viewAll')}
            </Link>
          )}
        </div>

        {/* Carousel Container */}
        <div className="relative group">
          {/* Left Arrow */}
          {showLeftArrow && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-9 w-9 md:h-11 md:w-11 rounded-full bg-white/95 dark:bg-card shadow-[0_2px_12px_rgba(0,0,0,0.15)] flex items-center justify-center text-foreground hover:bg-white hover:scale-110 active:scale-95 transition-all duration-200 ml-1 md:-translate-x-1/2 border border-border/80"
              aria-label={t('previous')}
            >
              <ChevronLeft className="h-5 w-5 md:h-6 md:w-6" />
            </button>
          )}

          {/* Products Grid */}
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex gap-3 md:gap-4 overflow-x-auto scrollbar-hide scroll-smooth pb-2"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {safeProducts.map((product) => {
              const discount = calculateDiscount(product.price, product.old_price || product.originalPrice);
              const productTitle = product.title_translated || product.title || product.name;
              const productImage = product.image || '/placeholder.jpg';
              const freeShipping = product.shipping_ammount === 0 || product.shipping_ammount === '0.00' || product.freeShipping;
              const rating = product.rating || product.average_rating || 0;
              const numPrice = parseFloat(product.price) || 0;

              return (
                <Link
                  key={product.id || product.pid}
                  to={`/detail/${product.slug || product.id}`}
                  className="flex-shrink-0 w-[180px] md:w-[240px] group/card"
                >
                  <div className="bg-card rounded-2xl overflow-hidden h-full flex flex-col border border-border/60 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_12px_40px_-12px_rgba(0,0,0,0.15)] hover:border-accent/30">
                    {/* Image Container */}
                    <div className="relative aspect-[4/5] overflow-hidden bg-gradient-to-b from-secondary/30 to-secondary/60">
                      <img
                        src={productImage}
                        alt={productTitle}
                        className="w-full h-full object-cover group-hover/card:scale-110 transition-transform duration-700 ease-out"
                        loading="lazy"
                      />
                      {/* Gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover/card:opacity-100 transition-opacity duration-300" />
                      
                      {discount && (
                        <span className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 bg-red-500 text-white text-[11px] font-bold px-2.5 py-1 rounded-full shadow-lg">
                          -{discount}%
                        </span>
                      )}
                      <button
                        className={`absolute top-2.5 right-2.5 h-8 w-8 rounded-full flex items-center justify-center transition-all hover:scale-110 shadow-sm ${
                          wishlistedIds[product.id || product.pid]
                            ? 'bg-red-50 border-2 border-red-200'
                            : 'bg-white/90 backdrop-blur-md border border-white/50 hover:bg-white'
                        }`}
                        aria-label={t('favorites')}
                        onClick={(e) => handleWishlistToggle(e, product)}
                        disabled={wishlistLoadingId === (product.id || product.pid)}
                      >
                        <Heart
                          className="h-4 w-4"
                          fill={wishlistedIds[product.id || product.pid] ? '#ef4444' : 'none'}
                          color={wishlistedIds[product.id || product.pid] ? '#ef4444' : '#9ca3af'}
                        />
                      </button>
                      
                      {/* Quick add to cart button on hover */}
                      <div className="absolute bottom-0 left-0 right-0 p-2.5 translate-y-full group-hover/card:translate-y-0 transition-transform duration-300 ease-out">
                        <button
                          className="w-full flex items-center justify-center gap-1.5 bg-primary text-primary-foreground py-2 rounded-lg text-xs font-semibold shadow-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                          onClick={(e) => handleAddToCart(e, product)}
                          disabled={addingProductId === (product.id || product.pid)}
                        >
                          <ShoppingCart className="h-3.5 w-3.5" />
                          {addingProductId === (product.id || product.pid)
                            ? '...'
                            : t('addToCart')}
                        </button>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-3 md:p-3.5 flex flex-col flex-1 gap-1">
                      {/* Price */}
                      <div className="mb-0.5">
                        <div className="flex items-baseline gap-1.5">
                          <p className="text-base md:text-lg font-bold text-foreground tracking-tight">
                            {formatPrice(product.price)}
                          </p>
                          {(product.old_price || product.originalPrice) && discount && (
                            <span className="text-xs text-muted-foreground line-through">
                              {formatPrice(product.old_price || product.originalPrice)}
                            </span>
                          )}
                        </div>

                      </div>

                      {/* Title */}
                      <h3 className="text-xs md:text-sm text-muted-foreground line-clamp-2 flex-1 leading-snug mt-auto">
                        {productTitle}
                      </h3>
                      
                      {/* Rating */}
                      {rating > 0 && (
                        <div className="flex items-center gap-1 mt-1">
                          <div className="flex">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className={`h-3 w-3 ${i < Math.round(rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`} />
                            ))}
                          </div>
                          <span className="text-[10px] text-muted-foreground">({rating})</span>
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Right Arrow */}
          {showRightArrow && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-9 w-9 md:h-11 md:w-11 rounded-full bg-white/95 dark:bg-card shadow-[0_2px_12px_rgba(0,0,0,0.15)] flex items-center justify-center text-foreground hover:bg-white hover:scale-110 active:scale-95 transition-all duration-200 mr-1 md:translate-x-1/2 border border-border/80"
              aria-label={t('next')}
            >
              <ChevronRight className="h-5 w-5 md:h-6 md:w-6" />
            </button>
          )}
        </div>

        {/* Mobile View All */}
        {showViewAll && (
          <div className="mt-4 md:hidden text-center">
            <Link to="/products" className="text-sm text-accent hover:underline font-medium">
              {t('viewAllProducts')}
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProductCarousel;
